import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icons } from '../lib/Icons';
import AppLayout from '../components/AppLayout';

const InterventionTestPage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', name: 'System Overview', Icon: Icons.ChartBar },
    { id: 'features', name: 'Key Features', Icon: Icons.Sparkles },
    { id: 'usage', name: 'How to Use', Icon: Icons.BookOpen },
    { id: 'demo', name: 'Live Demo', Icon: Icons.Target }
  ];

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Icons.Target className="w-8 h-8" /> Intervention Tracking System
              </h1>
              <p className="text-gray-600 mt-2">
                Comprehensive student intervention management and progress tracking
              </p>
            </div>
            <Link
              to="/interventions"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Launch System
            </Link>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b border-gray-200 rounded-lg shadow-sm mb-8">
          <div className="px-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.Icon className="w-4 h-4" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">System Overview</h2>
                <p className="text-gray-600 mb-4">
                  The Intervention Tracking System is designed specifically for SENCOs and educational staff 
                  to manage student interventions effectively. It provides a comprehensive platform for 
                  creating, monitoring, and tracking student progress across various intervention types.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="mb-2 flex justify-center"><Icons.Target className="w-8 h-8 text-blue-600" /></div>
                    <h3 className="font-semibold text-gray-900">Intervention Management</h3>
                    <p className="text-sm text-gray-600 mt-2">Create and manage student interventions with detailed parameters</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="mb-2 flex justify-center"><Icons.PencilSquare className="w-8 h-8 text-green-600" /></div>
                    <h3 className="font-semibold text-gray-900">Session Logging</h3>
                    <p className="text-sm text-gray-600 mt-2">Track attendance, activities, and progress for each session</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="mb-2 flex justify-center"><Icons.ChartBar className="w-8 h-8 text-purple-600" /></div>
                    <h3 className="font-semibold text-gray-900">Progress Monitoring</h3>
                    <p className="text-sm text-gray-600 mt-2">Monitor student progress and intervention effectiveness</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Key Features</h2>
                
                <div className="space-y-6">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Icons.Target className="w-5 h-5" /> Comprehensive Intervention Setup
                    </h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Multiple intervention types (Speech Therapy, OT, Reading Support, etc.)</li>
                      <li>• Flexible scheduling (daily, weekly, bi-weekly, monthly)</li>
                      <li>• Target area specification (Communication, Social Skills, Academic Skills)</li>
                      <li>• Multi-student assignment capabilities</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Icons.PencilSquare className="w-5 h-5" /> Detailed Session Logging
                    </h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Attendance tracking for each student</li>
                      <li>• Activity documentation and progress notes</li>
                      <li>• Challenge identification and next steps planning</li>
                      <li>• Comprehensive notes and observations</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Icons.ChartBar className="w-5 h-5" /> Advanced Analytics & Reporting
                    </h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Real-time dashboard with filtering options</li>
                      <li>• Progress tracking across multiple interventions</li>
                      <li>• Attendance and participation statistics</li>
                      <li>• Student-specific intervention history</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-orange-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Icons.MagnifyingGlass className="w-5 h-5" /> Smart Filtering & Search
                    </h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Filter by student, intervention type, or status</li>
                      <li>• Date range filtering (current, upcoming, past)</li>
                      <li>• Quick access to active interventions</li>
                      <li>• Comprehensive search capabilities</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'usage' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Use the System</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Step 1: Create an Intervention</h3>
                    <ol className="text-blue-800 space-y-1 ml-4">
                      <li>1. Navigate to the "Create Intervention" tab</li>
                      <li>2. Fill in intervention details (name, type, description)</li>
                      <li>3. Set start/end dates and frequency</li>
                      <li>4. Select target areas and assign students</li>
                      <li>5. Submit to create the intervention</li>
                    </ol>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-green-900 mb-2">Step 2: Log Sessions</h3>
                    <ol className="text-green-800 space-y-1 ml-4">
                      <li>1. Go to the "Log Session" tab</li>
                      <li>2. Select the intervention you want to log for</li>
                      <li>3. Record session details (date, time, duration)</li>
                      <li>4. Mark student attendance</li>
                      <li>5. Document activities, progress, and challenges</li>
                      <li>6. Submit to log the session</li>
                    </ol>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-900 mb-2">Step 3: Monitor Progress</h3>
                    <ol className="text-purple-800 space-y-1 ml-4">
                      <li>1. Use the Dashboard to see all interventions</li>
                      <li>2. Apply filters to focus on specific areas</li>
                      <li>3. View individual student progress</li>
                      <li>4. Track attendance and participation rates</li>
                      <li>5. Generate progress reports as needed</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'demo' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Live Demo</h2>
                
                <div className="text-center py-12">
                  <div className="mb-4 flex justify-center"><Icons.Target className="w-16 h-16 text-blue-600" /></div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Ready to Try the System?</h3>
                  <p className="text-gray-600 mb-6">
                    Click the button below to launch the live Intervention Tracking System and explore all the features.
                  </p>
                  <Link
                    to="/interventions"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Icons.Rocket className="w-5 h-5" /> Launch Intervention System
                  </Link>
                  <p className="text-sm text-gray-500 mt-4">
                    You can create test interventions, log sample sessions, and explore the dashboard
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Demo Tips:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Start by creating a sample intervention</li>
                    <li>• Try logging a few sessions to see the workflow</li>
                    <li>• Use the dashboard filters to explore different views</li>
                    <li>• Check out the student-specific intervention logs</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default InterventionTestPage; 